import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  Flag,
  Share2,
  Bell,
  BellOff,
  Paperclip,
  Send,
  MessageCircle,
  Loader2,
  Eye,
  X,
  Reply as ReplyIcon,
  Image,
  FileText,
  Download,
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getAuthHeader } from '../../services/otpAuthService';
import ReactionBar from './components/ReactionBar';
import ReplyCard from './components/ReplyCard';
import type { Reply } from './components/ReplyCard';
import MentionTextarea from './components/MentionTextarea';
import { useDiscussionSocket } from './hooks/useDiscussionSocket';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

const highlightMentions = (html: string) =>
  html.replace(
    /@([A-Za-zÀ-ÿ0-9][\w\s.'&-]*?)(?=\s|$|<|,|\.|\))/g,
    '<span class="mention-highlight bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 px-0.5 rounded font-medium">@$1</span>'
  );

/** Highlight search words in HTML without breaking tags */
const highlightSearchInHtml = (html: string, query: string): string => {
  if (!query.trim()) return html;
  const words = query.trim().split(/\s+/).filter(w => w.length >= 2);
  if (words.length === 0) return html;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  // Split by HTML tags to only highlight text nodes
  return html.replace(/(<[^>]+>)|([^<]+)/g, (match, tag, text) => {
    if (tag) return tag;
    return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-600/50 rounded-sm px-0.5">$1</mark>');
  });
};

/** Highlight search words in plain text, returns React nodes */
const highlightSearchText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return text;
  const words = query.trim().split(/\s+/).filter(w => w.length >= 2);
  if (words.length === 0) return text;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-600/50 rounded-sm px-0.5">{part}</mark>
      : part
  );
};

interface Attachment {
  originalName?: string;
  filename?: string;
  url: string;
  mimeType?: string;
  mimetype?: string;
  type?: string;
}

interface DiscussionDetail {
  _id: string;
  title: string;
  content: string;
  author: {
    _id: string;
    businessName: string;
    foto?: string;
    communityProfile?: {
      avatarColor?: string;
      level?: string;
    };
  } | null;
  area: {
    _id: string;
    slug: string;
    displayName: string;
    type?: string;
  };
  status: 'open' | 'resolved' | 'closed';
  isPinned: boolean;
  tags: string[];
  attachments?: Attachment[];
  reactions: Array<{ type: string; user: string }>;
  viewsCount: number;
  repliesCount: number;
  acceptedReply?: string;
  allowAcceptedReplies?: boolean;
  isFollowing: boolean;
  createdAt: string;
}

const timeAgo = (date: string) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'adesso';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} giorni fa`;
  return new Date(date).toLocaleDateString('it-IT');
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const isImage = (att: Attachment) => att.type === 'image' || (att.mimeType || att.mimetype || '').startsWith('image/');
const getAttUrl = (url: string) => url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/graphql', '') : 'http://localhost:5000'}${url}`;

const LEVEL_STYLES: Record<string, string> = {
  nuovo: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  attivo: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  esperto: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  top_contributor: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
};

const getLevelStyle = (level?: string) =>
  LEVEL_STYLES[(level || '').toLowerCase()] || LEVEL_STYLES.nuovo;

const getLevelLabel = (level?: string) => {
  const l = (level || '').toLowerCase();
  if (l === 'top_contributor') return 'Top Contributor';
  return l.charAt(0).toUpperCase() + l.slice(1) || 'Nuovo';
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open: { label: 'Aperta', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  resolved: { label: 'Risolta', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  closed: { label: 'Chiusa', color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
};

const DiscussionPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const hlQuery = searchParams.get('hl') || '';

  const [discussion, setDiscussion] = useState<DiscussionDetail | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingReplies, setLoadingReplies] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [replyContent, setReplyContent] = useState('');
  const [replyMentions, setReplyMentions] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [oldestPageLoaded, setOldestPageLoaded] = useState<number>(0);
  const [, setTotalPages] = useState(1);
  const [hasOlderReplies, setHasOlderReplies] = useState(false);
  const [loadingMoreReplies, setLoadingMoreReplies] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ id: string; authorName: string } | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ url: string; name: string; type: 'image' | 'pdf' } | null>(null);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const replyEditorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Real-time socket updates
  const onSocketNewReply = useCallback((reply: any) => {
    setReplies(prev => prev.some(r => r._id === reply._id) ? prev : [...prev, reply]);
    setDiscussion(prev => prev ? { ...prev, repliesCount: prev.repliesCount + 1 } : prev);
  }, []);

  const onSocketReactionUpdate = useCallback((reactions: any[]) => {
    setDiscussion(prev => prev ? { ...prev, reactions } : prev);
  }, []);

  useDiscussionSocket(id, onSocketNewReply, onSocketReactionUpdate);

  // Fetch discussion detail
  const fetchDiscussion = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${id}`, {
        headers: getAuthHeader() as any,
      });
      if (!res.ok) throw new Error('Not found');
      const result = await res.json();
      const d = result.data;
      setDiscussion(d);
      setFollowing(d.isFollowing || false);
      if (d.currentMemberId) setCurrentUserId(d.currentMemberId);
    } catch (err) {
      console.error('Error fetching discussion:', err);
      toast.error('Discussione non trovata');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [id]);

  // Fetch replies — loads last page first, then older pages on demand
  const REPLIES_PER_PAGE = 10;

  const fetchReplies = useCallback(
    async (page?: number) => {
      if (!id) return;
      const isInitial = page === undefined;
      if (isInitial) setLoadingReplies(true);
      else setLoadingMoreReplies(true);

      try {
        // First call: get last page. Subsequent calls: get specific page.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- URL conservato come riferimento della paginazione replies
        const url = isInitial
          ? `${API_BASE_URL}/api/community/v2/discussions/${id}/replies?page=1&limit=${REPLIES_PER_PAGE}`
          : `${API_BASE_URL}/api/community/v2/discussions/${id}/replies?page=${page}&limit=${REPLIES_PER_PAGE}`;

        // For initial load, first get total to calculate last page
        if (isInitial) {
          const countRes = await fetch(
            `${API_BASE_URL}/api/community/v2/discussions/${id}/replies?page=1&limit=1`,
            { headers: getAuthHeader() as any }
          );
          if (countRes.ok) {
            const countResult = await countRes.json();
            const total = countResult.pagination?.total || 0;
            const pages = Math.max(1, Math.ceil(total / REPLIES_PER_PAGE));
            setTotalPages(pages);

            // Now fetch last page
            const res = await fetch(
              `${API_BASE_URL}/api/community/v2/discussions/${id}/replies?page=${pages}&limit=${REPLIES_PER_PAGE}`,
              { headers: getAuthHeader() as any }
            );
            if (res.ok) {
              const result = await res.json();
              setReplies(result.data || []);
              setOldestPageLoaded(pages);
              setHasOlderReplies(pages > 1);
              if (result.currentMemberId && !currentUserId) {
                setCurrentUserId(result.currentMemberId);
              }
            }
          }
        } else {
          // Load older page — prepend
          const res = await fetch(
            `${API_BASE_URL}/api/community/v2/discussions/${id}/replies?page=${page}&limit=${REPLIES_PER_PAGE}`,
            { headers: getAuthHeader() as any }
          );
          if (res.ok) {
            const result = await res.json();
            setReplies((prev) => [...(result.data || []), ...prev]);
            setOldestPageLoaded(page!);
            setHasOlderReplies(page! > 1);
          }
        }
      } catch (err) {
        console.error('Error fetching replies:', err);
      } finally {
        setLoadingReplies(false);
        setLoadingMoreReplies(false);
      }
    },
    [id, currentUserId]
  );

  useEffect(() => {
    fetchDiscussion();
    fetchReplies();
  }, [fetchDiscussion, fetchReplies]);

  // React to discussion
  const handleDiscussionReact = async (type: string) => {
    if (!id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${id}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() } as any,
        body: JSON.stringify({ type }),
      });
      if (res.ok) {
        const result = await res.json();
        setDiscussion((prev) => (prev ? { ...prev, reactions: result.data || prev.reactions } : prev));
      }
    } catch (err) {
      toast.error('Errore nella reazione');
    }
  };

  // React to reply
  const handleReplyReact = async (replyId: string, type: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/replies/${replyId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() } as any,
        body: JSON.stringify({ type }),
      });
      if (res.ok) {
        const result = await res.json();
        setReplies((prev) =>
          prev.map((r) =>
            r._id === replyId ? { ...r, reactions: result.data || r.reactions } : r
          )
        );
      }
    } catch (err) {
      toast.error('Errore nella reazione');
    }
  };

  // Accept/unaccept reply (toggle)
  const handleAcceptReply = async (replyId: string) => {
    const reply = replies.find(r => r._id === replyId);
    const isAlreadyAccepted = reply?.isAccepted;

    if (!isAlreadyAccepted) {
      const confirmed = window.confirm(
        'Accettando questa risposta, la discussione verra marcata come "Risolta".\n\nVuoi continuare?'
      );
      if (!confirmed) return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/replies/${replyId}/accept`, {
        method: 'POST',
        headers: getAuthHeader() as any,
      });
      if (res.ok) {
        const result = await res.json();
        if (result.data?.isAccepted === false) {
          // Unaccepted
          setReplies((prev) => prev.map((r) => r._id === replyId ? { ...r, isAccepted: false } : r));
          // Check if any other reply is still accepted
          const anyAccepted = replies.some(r => r._id !== replyId && r.isAccepted);
          if (!anyAccepted) {
            setDiscussion((prev) => prev ? { ...prev, acceptedReply: undefined, status: 'open' } : prev);
          }
          toast.success('Risposta non piu accettata');
        } else {
          // Accepted
          setReplies((prev) => prev.map((r) => r._id === replyId ? { ...r, isAccepted: true } : r));
          setDiscussion((prev) => prev ? { ...prev, acceptedReply: replyId, status: 'resolved' } : prev);
          toast.success('Risposta accettata');
        }
      }
    } catch (err) {
      toast.error('Errore');
    }
  };

  // Follow/unfollow
  const handleFollow = async () => {
    if (!id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${id}/follow`, {
        method: 'POST',
        headers: getAuthHeader() as any,
      });
      if (res.ok) {
        const result = await res.json();
        setFollowing(result.data?.isFollowing ?? !following);
        toast.success(result.data?.isFollowing ? 'Stai seguendo la discussione' : 'Non segui piu la discussione');
      }
    } catch (err) {
      toast.error('Errore');
    }
  };

  // Copy link
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copiato negli appunti');
  };

  // Report discussion
  const handleReportDiscussion = async () => {
    const reason = window.prompt('Motivo della segnalazione:');
    if (!reason?.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${id}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() } as any,
        body: JSON.stringify({ reason: 'other', description: reason.trim() }),
      });
      if (res.ok) toast.success('Segnalazione inviata');
      else {
        const data = await res.json().catch(() => null);
        toast.error(data?.message || 'Errore');
      }
    } catch { toast.error('Errore nella segnalazione'); }
  };

  // Edit reply
  const handleEditReply = async (replyId: string, content: string) => {
    const res = await fetch(`${API_BASE_URL}/api/community/v2/replies/${replyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() } as any,
      body: JSON.stringify({ content }),
    });
    if (res.ok) {
      const result = await res.json();
      setReplies((prev) => prev.map((r) => r._id === replyId ? { ...r, content: result.data.content, editedAt: result.data.editedAt } : r));
      toast.success('Commento modificato');
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.message || 'Errore nella modifica');
      throw new Error('edit failed');
    }
  };

  // Delete reply
  const handleDeleteReply = async (replyId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/replies/${replyId}`, {
        method: 'DELETE',
        headers: getAuthHeader() as any,
      });
      if (res.ok) {
        setReplies((prev) => prev.filter((r) => r._id !== replyId));
        setDiscussion((prev) => prev ? { ...prev, repliesCount: Math.max(0, prev.repliesCount - 1) } : prev);
        toast.success('Commento eliminato');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || 'Errore nell\'eliminazione');
      }
    } catch { toast.error('Errore di connessione'); }
  };

  // Report reply
  const handleReportReply = async (replyId: string) => {
    const reason = window.prompt('Motivo della segnalazione:');
    if (!reason?.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/community/v2/replies/${replyId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() } as any,
        body: JSON.stringify({ reason: 'other', description: reason.trim() }),
      });
      if (res.ok) toast.success('Segnalazione inviata');
      else {
        const data = await res.json().catch(() => null);
        toast.error(data?.message || 'Errore');
      }
    } catch { toast.error('Errore nella segnalazione'); }
  };

  // Add files (from input or paste)
  const addFiles = (newFiles: File[]) => {
    const total = replyFiles.length + newFiles.length;
    if (total > 5) {
      toast.error('Massimo 5 allegati per risposta');
      return;
    }
    const maxSize = 10 * 1024 * 1024;
    for (const f of newFiles) {
      if (f.size > maxSize) {
        toast.error(`"${f.name}" supera 10 MB`);
        return;
      }
    }
    setReplyFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload files to server
  const uploadFiles = async (): Promise<any[]> => {
    if (replyFiles.length === 0) return [];
    const attachments: any[] = [];
    const images = replyFiles.filter((f) => f.type.startsWith('image/'));
    const docs = replyFiles.filter((f) => !f.type.startsWith('image/'));

    if (images.length > 0) {
      const fd = new FormData();
      images.forEach((f) => fd.append('files', f));
      const res = await fetch(`${API_BASE_URL}/api/community/v2/upload/images`, {
        method: 'POST',
        headers: getAuthHeader() as any,
        body: fd,
      });
      if (!res.ok) throw new Error('Errore upload immagini');
      const result = await res.json();
      attachments.push(...(result.data || []));
    }

    if (docs.length > 0) {
      const fd = new FormData();
      docs.forEach((f) => fd.append('files', f));
      const res = await fetch(`${API_BASE_URL}/api/community/v2/upload/documents`, {
        method: 'POST',
        headers: getAuthHeader() as any,
        body: fd,
      });
      if (!res.ok) throw new Error('Errore upload documenti');
      const result = await res.json();
      attachments.push(...(result.data || []));
    }

    return attachments;
  };

  // Submit reply
  const handleSubmitReply = async () => {
    if ((!replyContent.trim() && replyFiles.length === 0) || !id) return;
    setSubmitting(true);
    try {
      let attachments: any[] = [];
      if (replyFiles.length > 0) {
        setUploadingFiles(true);
        attachments = await uploadFiles();
        setUploadingFiles(false);
      }

      const res = await fetch(`${API_BASE_URL}/api/community/v2/discussions/${id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() } as any,
        body: JSON.stringify({ content: replyContent.trim(), mentions: replyMentions, parentReplyId: replyingTo?.id, attachments }),
      });
      if (res.ok) {
        const result = await res.json();
        setReplies((prev) => prev.some(r => r._id === result.data._id) ? prev : [...prev, result.data]);
        setReplyContent('');
        setReplyMentions([]);
        setReplyingTo(null);
        setReplyFiles([]);
        setDiscussion((prev) => (prev ? { ...prev, repliesCount: prev.repliesCount + 1 } : prev));
        toast.success('Risposta pubblicata');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || 'Errore nell\'invio della risposta');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  // Load older replies (previous page)
  const handleLoadOlderReplies = () => {
    if (oldestPageLoaded > 1) {
      fetchReplies(oldestPageLoaded - 1);
    }
  };

  // Reply to a specific reply
  const handleReplyToReply = (replyId: string, authorName: string) => {
    setReplyingTo({ id: replyId, authorName });
    replyEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin" />
      </div>
    );
  }

  // Not found
  if (!discussion) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-8 text-center">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Discussione non trovata</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            La discussione potrebbe essere stata rimossa o non sei autorizzato a visualizzarla.
          </p>
          <button
            onClick={() => navigate('/area-riservata/community-v2')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla Community
          </button>
        </div>
      </div>
    );
  }

  const isAuthor = discussion.author?._id === currentUserId;
  const canAcceptReplies = isAuthor && discussion.allowAcceptedReplies !== false;
  const avatarColor = discussion.author?.communityProfile?.avatarColor || '#6366f1';
  const initials = discussion.author?.businessName ? getInitials(discussion.author?.businessName) : 'UN';
  const statusInfo = STATUS_LABELS[discussion.status] || STATUS_LABELS.open;

  // No longer separate accepted replies — show all in order with badges
  const otherReplies = replies;

  // Build threaded structure: top-level replies + their children grouped underneath
  const threadedReplies = (() => {
    const childrenMap = new Map<string, Reply[]>();
    const topLevel: Reply[] = [];

    for (const r of otherReplies) {
      if (r.parentReply?._id) {
        const existing = childrenMap.get(r.parentReply._id) || [];
        existing.push(r);
        childrenMap.set(r.parentReply._id, existing);
      } else {
        topLevel.push(r);
      }
    }

    // Build result: each top-level reply followed by its children
    const result: Array<{ reply: Reply; children: Reply[] }> = [];
    for (const r of topLevel) {
      result.push({ reply: r, children: childrenMap.get(r._id) || [] });
    }

    // Orphaned children (parent not in current page) — show as top-level
    const usedParentIds = new Set(topLevel.map(r => r._id));
    childrenMap.forEach((orphans, parentId) => {
      if (!usedParentIds.has(parentId)) {
        orphans.forEach(c => result.push({ reply: c, children: [] }));
      }
    });

    return result;
  })();

  // Shared preview handler for ReplyCard
  const handleAttachmentPreview = (preview: { url: string; name: string; type: 'image' | 'pdf' }) => {
    setAttachmentPreview(preview);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950">
      {/* Attachment preview modal */}
      {attachmentPreview && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setAttachmentPreview(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-white/80 truncate max-w-[70%] font-medium">{attachmentPreview.name}</span>
              <div className="flex items-center gap-1">
                <a
                  href={attachmentPreview.url}
                  download={attachmentPreview.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                  title="Scarica"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button
                  onClick={() => setAttachmentPreview(null)}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {attachmentPreview.type === 'image' ? (
              <img
                src={attachmentPreview.url}
                alt={attachmentPreview.name}
                className="max-h-[80vh] w-auto mx-auto rounded-xl object-contain shadow-2xl"
              />
            ) : (
              <iframe
                src={attachmentPreview.url}
                title={attachmentPreview.name}
                className="w-full h-[80vh] rounded-xl bg-white shadow-2xl"
              />
            )}
          </div>
        </div>
      )}

      {/* Top bar with breadcrumb */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 h-12 flex items-center gap-2 text-sm overflow-x-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          <Link
            to="/area-riservata/community-v2"
            className="text-[13px] text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 whitespace-nowrap"
          >
            Community
          </Link>
          <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <Link
            to={`/area-riservata/community-v2/area/${discussion.area.slug}`}
            className="text-[13px] text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 whitespace-nowrap"
          >
            {discussion.area.displayName}
          </Link>
        </div>
      </div>

      {hlQuery && (
        <div className="max-w-3xl mx-auto px-4 pt-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-xs text-yellow-800 dark:text-yellow-300">
            <span>Evidenziazione per: <strong>"{hlQuery}"</strong></span>
            <button
              onClick={() => navigate(`/area-riservata/community-v2/discussione/${id}`, { replace: true })}
              className="ml-auto p-0.5 hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded transition-colors"
              title="Rimuovi evidenziazione"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-4">
        {/* ═══════════ ORIGINAL POST CARD ═══════════ */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 mb-4 overflow-hidden">
          <div className="p-5 sm:p-6">
            {/* Author row */}
            <div className="flex items-center gap-3 mb-4">
              {discussion.author?.foto ? (
                <img
                  src={getAttUrl(discussion.author.foto)}
                  alt={discussion.author.businessName}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-100 dark:ring-gray-700"
                />
              ) : (
                <span
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ring-2 ring-white/20"
                  style={{ backgroundColor: avatarColor }}
                >
                  {initials}
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {discussion.author?.businessName || 'UNASCA'}
                  </span>
                  {discussion.author?.communityProfile?.level && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${getLevelStyle(discussion.author.communityProfile.level)}`}>
                      {getLevelLabel(discussion.author.communityProfile.level)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                  <span>{timeAgo(discussion.createdAt)}</span>
                  <span className="text-gray-300 dark:text-gray-600">&middot;</span>
                  <span className="flex items-center gap-0.5">
                    <Eye className="w-3 h-3" /> {discussion.viewsCount || 0} visualizzazioni
                  </span>
                </div>
              </div>
              {(discussion.allowAcceptedReplies !== false || discussion.status !== 'open') && (
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-[15px] font-bold text-gray-900 dark:text-white mb-2 leading-snug">
              {hlQuery ? highlightSearchText(discussion.title, hlQuery) : discussion.title}
            </h1>

            {/* Tags */}
            {discussion.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {discussion.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            <div
              className="testo-ricco text-gray-800 dark:text-gray-200"
              dangerouslySetInnerHTML={{ __html: hlQuery ? highlightSearchInHtml(highlightMentions(discussion.content), hlQuery) : highlightMentions(discussion.content) }}
            />
          </div>

          {/* Attachments — full-width, outside padding for edge-to-edge images */}
          {discussion.attachments && discussion.attachments.length > 0 && (
            <div className={`px-5 sm:px-6 pb-4 ${discussion.attachments.filter(a => isImage(a)).length === 1 ? '' : 'flex flex-wrap gap-2'}`}>
              {discussion.attachments.map((att, i) => {
                const url = getAttUrl(att.url);
                const name = att.originalName || att.filename || 'file';
                const isPdf = (att.mimeType || att.mimetype || '').includes('pdf') || name.toLowerCase().endsWith('.pdf');
                const imageAtts = discussion.attachments!.filter(a => isImage(a));
                return isImage(att) ? (
                  <button
                    key={i}
                    onClick={() => setAttachmentPreview({ url, name, type: 'image' })}
                    className={`block overflow-hidden rounded-xl hover:opacity-95 transition-opacity cursor-pointer ${
                      imageAtts.length === 1
                        ? 'w-full max-h-[400px]'
                        : 'w-[calc(50%-4px)] max-h-[200px]'
                    }`}
                  >
                    <img src={url} alt={name} className="w-full h-full object-cover" />
                  </button>
                ) : isPdf ? (
                  <button
                    key={i}
                    onClick={() => setAttachmentPreview({ url, name, type: 'pdf' })}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                  >
                    <FileText className="w-4 h-4 text-red-500" />
                    {name}
                  </button>
                ) : (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Paperclip className="w-4 h-4" />
                    {name}
                  </a>
                );
              })}
            </div>
          )}

          {/* Action bar */}
          <div className="border-t border-gray-100 dark:border-gray-800 px-3 py-1.5 flex items-center gap-0.5">
            <div className="flex-1 px-1">
              <ReactionBar
                reactions={discussion.reactions}
                currentUserId={currentUserId}
                onReact={handleDiscussionReact}
                facebook
                extended={discussion.area?.type === 'lounge'}
              />
            </div>
            <button
              onClick={handleFollow}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium rounded-lg transition-all ${
                following
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/10 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {following ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
              {following ? 'Seguita' : 'Segui'}
            </button>
            <button
              onClick={handleShare}
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
              Condividi
            </button>
            <button
              onClick={handleReportDiscussion}
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-gray-400 dark:text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/10 hover:text-red-500 dark:hover:text-red-400 rounded-lg transition-colors"
            >
              <Flag className="w-3.5 h-3.5" />
              Segnala
            </button>
          </div>
        </div>

        {/* ═══════════ COMMENTS SECTION ═══════════ */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          {/* Comments header */}
          <div className="px-5 pt-4 pb-2 flex items-center gap-2">
            <h2 className="text-[13px] font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-gray-400" />
              Risposte
            </h2>
            <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full tabular-nums">
              {discussion.repliesCount}
            </span>
          </div>

          {/* Comments list */}
          <div className="px-5 pb-3">
            {loadingReplies ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-indigo-600"></div>
              </div>
            ) : otherReplies.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Nessuna risposta ancora. Sii il primo a rispondere!
                </p>
              </div>
            ) : (
              <div className="space-y-3 py-1">
                {hasOlderReplies && (
                  <div className="pb-2">
                    <button
                      onClick={handleLoadOlderReplies}
                      disabled={loadingMoreReplies}
                      className="text-[13px] font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors disabled:opacity-50"
                    >
                      {loadingMoreReplies ? 'Caricamento...' : `Visualizza commenti precedenti...`}
                    </button>
                  </div>
                )}
                {threadedReplies.map(({ reply, children }) => (
                  <div key={reply._id} className="relative">
                    {/* Vertical line from parent avatar to children */}
                    {children.length > 0 && (
                      <div className="absolute left-[15px] top-[36px] bottom-0 w-[2px] bg-gray-200 dark:bg-gray-700 rounded-full" />
                    )}
                    {/* Parent comment */}
                    <ReplyCard
                      reply={reply}
                      currentUserId={currentUserId}
                      isDiscussionAuthor={canAcceptReplies}
                      onReact={handleReplyReact}
                      onAccept={handleAcceptReply}
                      onReport={handleReportReply}
                      onReplyToReply={handleReplyToReply}
                      onPreview={handleAttachmentPreview}
                      onEdit={handleEditReply}
                      onDelete={handleDeleteReply}
                      extendedReactions={discussion?.area?.type === 'lounge'}
                    />
                    {/* Nested children */}
                    {children.length > 0 && (
                      <div className="ml-10 mt-2 space-y-2">
                        {children.map((child, idx) => (
                          <div key={child._id} className="relative">
                            {/* Horizontal connector: from vertical line to child avatar */}
                            <div className="absolute -left-[25px] top-[14px] w-[21px] h-[2px] bg-gray-200 dark:bg-gray-700 rounded-full" />
                            {/* Curve from vertical to horizontal */}
                            <div className="absolute -left-[25px] top-0 w-[2px] h-[14px] bg-gray-200 dark:bg-gray-700 rounded-full" />
                            <ReplyCard
                              reply={child}
                              currentUserId={currentUserId}
                              isDiscussionAuthor={canAcceptReplies}
                              onReact={handleReplyReact}
                              onAccept={handleAcceptReply}
                              onReport={handleReportReply}
                              onReplyToReply={handleReplyToReply}
                              onPreview={handleAttachmentPreview}
                              onEdit={handleEditReply}
                              onDelete={handleDeleteReply}
                              extendedReactions={discussion?.area?.type === 'lounge'}
                              isChild
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* No load-more at bottom — older replies load from top */}
          </div>

          {/* Reply editor — inline at bottom of comments card */}
          {discussion.status !== 'closed' && (
            <div ref={replyEditorRef} className="border-t border-gray-100 dark:border-gray-800 px-5 py-3">
              {replyingTo && (
                <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-xs text-indigo-700 dark:text-indigo-300">
                  <ReplyIcon className="w-3.5 h-3.5" />
                  <span>In risposta a <strong>@{replyingTo.authorName}</strong></span>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="ml-auto p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <div className="flex gap-2.5">
                {/* Current user avatar placeholder */}
                <div className="flex-shrink-0 pt-1">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[11px] font-bold">
                    Tu
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <MentionTextarea
                    value={replyContent}
                    onChange={setReplyContent}
                    onMentionsChange={setReplyMentions}
                    onPasteFiles={addFiles}
                    placeholder="Scrivi un commento..."
                    rows={2}
                    className="w-full px-3.5 py-2.5 text-[13px] border border-gray-200 dark:border-gray-600 rounded-2xl bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 resize-none transition-colors"
                  />

                  {/* File preview */}
                  {replyFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {replyFiles.map((file, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-[11px] text-gray-600 dark:text-gray-300"
                        >
                          {file.type.startsWith('image/') ? (
                            <Image className="w-3 h-3 text-indigo-500" />
                          ) : (
                            <FileText className="w-3 h-3 text-orange-500" />
                          )}
                          <span className="max-w-[100px] truncate">{file.name}</span>
                          <button
                            onClick={() => removeFile(i)}
                            className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) addFiles(Array.from(e.target.files));
                      e.target.value = '';
                    }}
                  />
                  <div className="flex items-center gap-1 mt-1.5">
                    <button
                      className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-indigo-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                      title="Allega file"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <div className="flex-1" />
                    <button
                      onClick={handleSubmitReply}
                      disabled={submitting || (!replyContent.trim() && replyFiles.length === 0)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full transition-colors text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {uploadingFiles ? 'Upload...' : ''}
                        </>
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                      Rispondi
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {discussion.status === 'closed' && (
            <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-4 text-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Discussione chiusa — non e possibile aggiungere nuove risposte.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscussionPage;
